import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  GOLDEN_VALLEY_INTERNET,
  GOLDEN_VALLEY_PHONE,
  KINGMAN_INTERNET,
  KINGMAN_PHONE,
  LocalCitySeo,
} from '../data/localSeoContent';

const SITE_URL = 'https://airmaxcm.com';
const OG_IMAGE = `${SITE_URL}/images/AirmaxLogo2026.png`;
const DEFAULT_TITLE =
  'Airmax Internet | Internet Provider in Kingman & Golden Valley, AZ';
const DEFAULT_DESCRIPTION =
  'Local ISP serving Kingman and Golden Valley, Arizona with high-speed wireless internet and WAVE Fiber Speeds, free installation on featured plans, and Arizona-based support at 928-565-6707.';

const PAGE_META: Record<string, { title: string; description: string; noindex?: boolean }> = {
  '/': {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
  },
  '/about': {
    title: 'About Airmax Internet | Kingman & Golden Valley ISP',
    description:
      'Airmax Internet is a local Arizona ISP serving Kingman, Golden Valley, and nearby Mohave County communities with wireless internet and WAVE Fiber Speeds.',
  },
  '/plans': {
    title: 'Internet Plans in Kingman & Golden Valley, AZ | Airmax',
    description:
      'Compare Airmax wireless internet plans with WAVE Fiber Speeds for homes and businesses in Kingman, Golden Valley, and surrounding Arizona communities.',
  },
  '/phone-service': {
    title: 'Phone Service in Kingman & Golden Valley, AZ | Airmax',
    description:
      'Airmax residential and business phone service with number porting, unlimited calling, and local support in Kingman and Golden Valley, Arizona.',
  },
  '/phone-kingman-az': {
    title: 'Phone Service in Kingman, AZ | Airmax Internet',
    description:
      'Home and business phone service in Kingman, Arizona from Airmax. Unlimited calling, number porting, and Arizona-based support at 928-565-6707.',
  },
  '/phone-golden-valley-az': {
    title: 'Phone Service in Golden Valley, AZ | Airmax Internet',
    description:
      'Home and business phone service in Golden Valley, Arizona from Airmax. Unlimited calling, number porting, and local support.',
  },
  '/check-service': {
    title: 'Check Internet Availability in Kingman & Golden Valley, AZ',
    description:
      'Check whether Airmax Internet is available at your Kingman or Golden Valley, Arizona address.',
  },
  '/support': {
    title: 'Customer Support | Airmax Internet Arizona',
    description:
      'Get help with your Airmax Internet connection, equipment, billing, or account. Call 928-565-6707.',
  },
  '/pay-bill': {
    title: 'Pay Your Bill | Airmax Internet',
    description:
      'Pay your Airmax Internet invoice online. Verify with your account phone number, then open your secure one-time payment link.',
  },
  '/payment-extension': {
    title: 'Payment Extension | Airmax Internet',
    description:
      'Need more time to pay? Verify with your account phone, then extend an unpaid Airmax invoice due date.',
  },
  '/tower-status': {
    title: 'Network Status | Airmax Internet Kingman & Golden Valley',
    description:
      'View current Airmax Internet network and tower status across Golden Valley and Kingman, Arizona.',
  },
  '/contact': {
    title: 'Contact Airmax Internet | Kingman & Golden Valley, AZ',
    description:
      'Contact Airmax Internet for sales, service availability, billing, and technical support in Kingman and Golden Valley.',
  },
  '/chat': {
    title: 'Chat with Airmax Support | Kingman & Golden Valley, AZ',
    description:
      'Start a live Airmax support chat. Enter your phone number to connect with our Arizona-based support team.',
  },
  '/privacy': {
    title: 'Privacy Policy | Airmax Internet',
    description: 'Read the Airmax Internet privacy policy.',
  },
  '/terms': {
    title: 'Terms and Conditions | Airmax Internet',
    description: 'Read the terms and conditions for Airmax Internet services.',
  },
  '/how-it-works': {
    title: 'How Airmax Internet Works in Arizona',
    description:
      'Learn how Airmax delivers reliable wireless internet with WAVE Fiber Speeds to homes and businesses in Kingman and Golden Valley.',
  },
  '/gamers': {
    title: 'Internet for Gaming | Airmax Internet Arizona',
    description:
      'Explore Airmax internet options for responsive online gaming in Kingman, Golden Valley, and nearby communities.',
  },
  '/internet-kingman-az': {
    title: 'Internet in Kingman, AZ | Airmax Internet Provider',
    description:
      'High-speed wireless internet with WAVE Fiber Speeds in Kingman, Arizona from Airmax. Local ISP, free installation on featured plans, unlimited data. Call 928-565-6707.',
  },
  '/internet-golden-valley-az': {
    title: 'Internet in Golden Valley, AZ | Airmax Internet Provider',
    description:
      'High-speed wireless internet in Golden Valley, Arizona from Airmax. WAVE Fiber Speeds, local ISP support. Check availability today.',
  },
  '/service-acknowledgment': {
    title: 'Service Acknowledgment | Airmax Internet',
    description: 'Airmax customer service acknowledgment and electronic signature form.',
    noindex: true,
  },
};

const CITY_SEO_BY_PATH: Record<string, LocalCitySeo> = {
  '/internet-kingman-az': KINGMAN_INTERNET,
  '/internet-golden-valley-az': GOLDEN_VALLEY_INTERNET,
  '/phone-kingman-az': KINGMAN_PHONE,
  '/phone-golden-valley-az': GOLDEN_VALLEY_PHONE,
};

const LOCAL_BUSINESS_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'InternetServiceProvider',
  '@id': `${SITE_URL}/#business`,
  name: 'Airmax Internet',
  legalName: 'Airmax Internet LLC',
  url: SITE_URL,
  logo: `${SITE_URL}/images/logo2023.png`,
  image: `${SITE_URL}/images/AirmaxLogo2026.png`,
  telephone: '+1-928-565-6707',
  email: 'airmaxhelpdesk@gmail.com',
  priceRange: '$$',
  areaServed: [
    { '@type': 'City', name: 'Kingman', containedInPlace: { '@type': 'State', name: 'Arizona' } },
    { '@type': 'City', name: 'Golden Valley', containedInPlace: { '@type': 'State', name: 'Arizona' } },
    { '@type': 'AdministrativeArea', name: 'Mohave County' },
  ],
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Golden Valley',
    addressRegion: 'AZ',
    postalCode: '86413',
    addressCountry: 'US',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 35.2228,
    longitude: -114.2197,
  },
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '09:00',
      closes: '17:00',
    },
  ],
  sameAs: ['https://www.facebook.com/profile.php?id=61552173193565'],
  description: DEFAULT_DESCRIPTION,
  knowsAbout: [
    'Wireless internet Kingman AZ',
    'Internet Golden Valley Arizona',
    'WAVE Fiber Speeds',
    'Business phone service Arizona',
  ],
};

function setMeta(selector: string, attribute: string, value: string): void {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement('meta');
    const match = selector.match(/\[(name|property)="([^"]+)"\]/);
    if (match) element.setAttribute(match[1], match[2]);
    document.head.appendChild(element);
  }
  element.setAttribute(attribute, value);
}

function upsertJsonLd(id: string, data: Record<string, unknown> | null): void {
  if (!data) {
    document.getElementById(id)?.remove();
    return;
  }
  let script = document.getElementById(id) as HTMLScriptElement | null;
  if (!script) {
    script = document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data);
}

function buildBreadcrumbs(path: string, title: string) {
  const items: { name: string; item: string }[] = [
    { name: 'Home', item: `${SITE_URL}/` },
  ];
  if (path.startsWith('/internet-')) {
    items.push({ name: 'Internet Plans', item: `${SITE_URL}/plans` });
  } else if (path.startsWith('/phone-') && path !== '/phone-service') {
    items.push({ name: 'Phone Service', item: `${SITE_URL}/phone-service` });
  }
  if (path !== '/') {
    items.push({ name: title.replace(/\s*\|\s*Airmax.*$/i, '').trim(), item: `${SITE_URL}${path}` });
  }
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((entry, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: entry.name,
      item: entry.item,
    })),
  };
}

function buildFaqSchema(content: LocalCitySeo) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: content.faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

function buildServiceSchema(path: string, meta: { title: string; description: string }, content: LocalCitySeo) {
  const isPhone = content.service === 'phone';
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: meta.title,
    description: meta.description,
    url: `${SITE_URL}${path}`,
    provider: { '@id': `${SITE_URL}/#business` },
    areaServed: {
      '@type': 'City',
      name: content.city,
      containedInPlace: { '@type': 'State', name: 'Arizona' },
    },
    serviceType: isPhone ? 'TelephoneService' : 'InternetService',
    category: isPhone ? 'Phone Service' : 'Internet Service Provider',
  };
}

const Seo: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const normalizedPath =
      pathname === '/' ? '/' : pathname.replace(/\/+$/, '').toLowerCase();
    const meta = PAGE_META[normalizedPath] ?? {
      title: '404 Page Not Found | Airmax Internet',
      description: 'The requested page could not be found.',
      noindex: true,
    };
    const canonical = `${SITE_URL}${normalizedPath === '/' ? '/' : normalizedPath}`;
    const cityContent = CITY_SEO_BY_PATH[normalizedPath];

    document.title = meta.title;

    let canonicalLink = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = canonical;

    setMeta('meta[name="description"]', 'content', meta.description);
    setMeta('meta[name="robots"]', 'content', meta.noindex ? 'noindex, nofollow' : 'index, follow');
    setMeta('meta[property="og:title"]', 'content', meta.title);
    setMeta('meta[property="og:description"]', 'content', meta.description);
    setMeta('meta[property="og:url"]', 'content', canonical);
    setMeta('meta[property="og:image"]', 'content', OG_IMAGE);
    setMeta('meta[property="og:type"]', 'content', 'website');
    setMeta('meta[name="twitter:title"]', 'content', meta.title);
    setMeta('meta[name="twitter:description"]', 'content', meta.description);
    setMeta('meta[name="twitter:url"]', 'content', canonical);
    setMeta('meta[name="twitter:image"]', 'content', OG_IMAGE);

    upsertJsonLd('airmax-local-business-schema', LOCAL_BUSINESS_SCHEMA);
    upsertJsonLd('airmax-breadcrumb-schema', buildBreadcrumbs(normalizedPath, meta.title));

    if (cityContent) {
      upsertJsonLd('airmax-webpage-schema', {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: meta.title,
        description: meta.description,
        url: canonical,
        about: {
          '@type': 'City',
          name: cityContent.city,
          containedInPlace: { '@type': 'State', name: 'Arizona' },
        },
        provider: { '@id': `${SITE_URL}/#business` },
        isPartOf: { '@type': 'WebSite', name: 'Airmax Internet', url: SITE_URL },
      });
      upsertJsonLd('airmax-service-schema', buildServiceSchema(normalizedPath, meta, cityContent));
      upsertJsonLd('airmax-faq-schema', buildFaqSchema(cityContent));
    } else {
      upsertJsonLd('airmax-webpage-schema', null);
      upsertJsonLd('airmax-service-schema', null);
      upsertJsonLd('airmax-faq-schema', null);
    }
  }, [pathname]);

  return null;
};

export default Seo;
